import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Box, Typography, Paper, CircularProgress, Chip,
  List, ListItem, ListItemText, ListItemAvatar, Avatar, Divider,
  Button, FormControl, InputLabel, Select, MenuItem, Alert,
  Card, CardContent
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Groups as MeetingIcon,
  Event as CalendarIcon,
  Person as TeacherIcon,
  ChildCare as ChildIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { parentAPI } from '../../services/api';
import EmptyState from '../../components/EmptyState';
import MobilePageHeader from '@components/mobile/MobilePageHeader';

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-IN', {
    weekday: 'short', day: 'numeric', month: 'short',
    year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

const statusConfig = {
  Scheduled: { color: 'primary', label: 'Scheduled' },
  Completed: { color: 'success', label: 'Completed' },
  Cancelled: { color: 'error', label: 'Cancelled' },
  Rescheduled: { color: 'warning', label: 'Rescheduled' }
};

const isPast = (dateStr) => dateStr && new Date(dateStr) < new Date();

const ParentMeetings = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [meetings, setMeetings] = useState([]);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const fetchMeetings = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const res = await parentAPI.getMeetings(params);
      setMeetings(res.data.meetings || []);
      setError('');
    } catch (err) {
      setError('Failed to load meetings.');
      console.error('Parent meetings error:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchMeetings(); }, [fetchMeetings]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const upcoming = meetings.filter(m => m.status === 'Scheduled' && !isPast(m.scheduled_datetime));
  const past = meetings.filter(m => m.status !== 'Scheduled' || isPast(m.scheduled_datetime));

  const MeetingCard = ({ meeting }) => {
    const sc = statusConfig[meeting.status] || { color: 'default', label: meeting.status };
    return (
      <Card elevation={1} sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <ChildIcon fontSize="small" color="primary" />
                <Typography variant="subtitle1" fontWeight={600}>
                  {meeting.student?.name || 'Student'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ({meeting.student?.class?.name || ''})
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <TeacherIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  Teacher: {meeting.teacher?.user?.name || '—'}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <CalendarIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {formatDateTime(meeting.scheduled_datetime)}
                </Typography>
              </Box>
            </Box>
            <Chip label={sc.label} color={sc.color} size="small" />
          </Box>

          {meeting.meeting_agenda && (
            <Box mt={1.5} p={1.5} bgcolor="grey.50" borderRadius={1}>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                Agenda
              </Typography>
              <Typography variant="body2">{meeting.meeting_agenda}</Typography>
            </Box>
          )}

          {meeting.notes && meeting.status === 'Completed' && (
            <Box mt={1.5} p={1.5} bgcolor="success.50" borderRadius={1}>
              <Typography variant="caption" color="success.dark" display="block" mb={0.5}>
                Meeting Notes
              </Typography>
              <Typography variant="body2">{meeting.notes}</Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <MobilePageHeader title="Parent-Teacher Meetings" onBack={() => navigate('/parent/dashboard')} />
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          gap={2}
          mb={3}
        >
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 2, flex: 1 }}>
            <Button variant="outlined" startIcon={<BackIcon />} onClick={() => navigate('/parent/dashboard')}>
              Back to Dashboard
            </Button>
            <Typography variant="h5" fontWeight={700} flex={1}>
              Parent-Teacher Meetings
            </Typography>
          </Box>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 140 } }}>
            <InputLabel>Filter</InputLabel>
            <Select
              value={statusFilter}
              label="Filter"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Scheduled">Scheduled</MenuItem>
              <MenuItem value="Completed">Completed</MenuItem>
              <MenuItem value="Cancelled">Cancelled</MenuItem>
              <MenuItem value="Rescheduled">Rescheduled</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : meetings.length === 0 ? (
          statusFilter ? (
            <EmptyState
              icon={MeetingIcon}
              title="No meetings found"
              description="Try a different filter."
              variant="no-results"
            />
          ) : (
            <EmptyState
              icon={MeetingIcon}
              title="No meetings found"
              description="Meetings scheduled by your child's teacher will appear here."
            />
          )
        ) : (
          <>
            {!statusFilter && upcoming.length > 0 && (
              <>
                <Typography variant="h6" fontWeight={600} mb={2} color="primary.main">
                  Upcoming ({upcoming.length})
                </Typography>
                {upcoming.map(m => <MeetingCard key={m.id} meeting={m} />)}
                {past.length > 0 && (
                  <Typography variant="h6" fontWeight={600} mt={3} mb={2} color="text.secondary">
                    Past Meetings ({past.length})
                  </Typography>
                )}
              </>
            )}
            {(statusFilter ? meetings : past).map(m => <MeetingCard key={m.id} meeting={m} />)}
          </>
        )}
      </Container>
  );
};

export default ParentMeetings;
